'use client';

import { FC, MouseEvent, useMemo } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { useStoreBoot } from '@/api/useStoreBoot';
import { useTrackPublisherEvents } from '@/api/useTrackPublisherEvents';
import { LanguagePicker } from '@/app/appcharge/LanguagePicker';
import { CardContent } from '@/components/ui/card';
import { isIOSVersionBelow } from '@/lib/device';
import { EEventsType, EPublisherEvents } from '@/lib/enums';
import { enrichOptions } from '@/lib/eventDataUtils';
import { trackMixpanelEvent } from '@/lib/mixpanelClient';
import { ensureAbsoluteUrl } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';

import { useTranslations } from 'next-intl';

import { FooterLinkModal } from './FooterLinkModal';
import {
  getFooterLinkTestId,
  PortalFooterLink,
  resolveStoreFooterLinks,
} from './footerLinks.utils';
import { StyledFooter, useFooterScopeProps } from './footerStyle';

const FOOTER_LINK_CLASS_NAME = 'footer-link text-[14px] font-normal hover:text-primary';
// Below this iOS version the iframe modal is unreliable, so the store opens modal links in a new tab.
const MIN_IOS_VERSION_FOR_LINK_MODAL = 16;

const Footer: FC = () => {
  const t = useTranslations();
  const { initialBoot, languagesList, playerData, openInfoModal } = useAppState();
  const { getBoot } = useStoreBoot();
  const storeFooterLinks = resolveStoreFooterLinks(getBoot.data, playerData);
  const isStoreFooterLoading = getBoot.isLoading;
  const {
    supportUrl: rawSupportUrl,
    termsOfServiceUrl: rawTermsOfServiceUrl,
    privacyPolicyUrl: rawPrivacyPolicyUrl,
  } = initialBoot?.theme?.footer ?? {};
  const supportUrl = rawSupportUrl ? ensureAbsoluteUrl(rawSupportUrl) : undefined;
  const termsOfServiceUrl = rawTermsOfServiceUrl
    ? ensureAbsoluteUrl(rawTermsOfServiceUrl)
    : undefined;
  const privacyPolicyUrl = rawPrivacyPolicyUrl ? ensureAbsoluteUrl(rawPrivacyPolicyUrl) : undefined;
  const pathname = usePathname();
  const isSupportPage = pathname?.startsWith('/support');
  const { platform } = useAppState();
  const { trackPublisherUIEvent } = useTrackPublisherEvents();
  const footerScopeProps = useFooterScopeProps();

  const shouldShowLanguagePicker = useMemo(() => {
    return (
      languagesList && languagesList?.length > 1 && initialBoot?.theme?.header?.showTranslations
    );
  }, [languagesList, initialBoot?.theme?.header?.showTranslations]);

  const handleLinkClick = (linkUrl: string, linkText: string) => {
    trackMixpanelEvent(EEventsType.PORTAL_FOOTER_CLICKED, {
      device_platform: platform,
      footer_button: linkText,
      footer_url: linkUrl,
    });

    trackPublisherUIEvent.mutate({
      ...enrichOptions({
        platform,
        eventName: EPublisherEvents.PORTAL_FOOTER_CLICKED,
        footer: { button: linkText, url: linkUrl },
      }),
    });
  };

  const resolveLinkLabel = ({ label, translationKey }: PortalFooterLink): string =>
    t.has(translationKey) ? t(translationKey) : label;

  const renderStoreFooterLink = (link: PortalFooterLink) => {
    const label = resolveLinkLabel(link);
    const linkProps = {
      'data-testid': getFooterLinkTestId(link.key),
      className: FOOTER_LINK_CLASS_NAME,
    };
    const handleClick = () => handleLinkClick(link.url, label);

    if (link.target === 'page') {
      return (
        <Link key={link.key} href={link.url} onClick={handleClick} {...linkProps}>
          {label}
        </Link>
      );
    }

    if (link.target === 'newTab') {
      return (
        <a
          key={link.key}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          {...linkProps}
        >
          {label}
        </a>
      );
    }

    const handleModalClick = (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      handleClick();
      if (isIOSVersionBelow(MIN_IOS_VERSION_FOR_LINK_MODAL)) {
        window.open(link.url, '_blank', 'noopener,noreferrer');
        return;
      }
      openInfoModal(link.url);
    };

    return (
      <a key={link.key} href={link.url} onClick={handleModalClick} {...linkProps}>
        {label}
      </a>
    );
  };

  const legacyLinks = (
    <>
      {supportUrl && (
        <a
          href={supportUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="PortalFooter-SupportLink"
          onClick={() => handleLinkClick(supportUrl, t('portal.footer.supportButton'))}
          className={FOOTER_LINK_CLASS_NAME}
        >
          {t('portal.footer.supportButton')}
        </a>
      )}
      {termsOfServiceUrl && (
        <a
          href={termsOfServiceUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="PortalFooter-TermsLink"
          onClick={() => handleLinkClick(termsOfServiceUrl, t('portal.footer.termsButton'))}
          className={FOOTER_LINK_CLASS_NAME}
        >
          {t('portal.footer.termsButton')}
        </a>
      )}
      {privacyPolicyUrl && (
        <a
          href={privacyPolicyUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => handleLinkClick(privacyPolicyUrl, t('portal.footer.privacyButton'))}
          data-testid="PortalFooter-PrivacyLink"
          className={FOOTER_LINK_CLASS_NAME}
        >
          {t('portal.footer.privacyButton')}
        </a>
      )}
    </>
  );

  const renderFooterLinks = () => {
    if (storeFooterLinks) return storeFooterLinks.map(renderStoreFooterLink);
    if (isStoreFooterLoading) return null;
    return legacyLinks;
  };

  return (
    <>
      <StyledFooter
        {...footerScopeProps}
        $isTransparent={isSupportPage}
        data-testid="PortalFooter-Container"
        className="footer-container md:w-full rounded-xl border-0 mx-[18px] mb-[18px] md:mx-0 md:mb-0"
      >
        <CardContent className="footer-content flex flex-col items-center gap-2 py-3 text-center md:py-6">
          <div className="flex flex-col items-center gap-2 md:w-full md:flex-row md:justify-between">
            <div className="order-2 flex items-center gap-2 md:order-1">
              <Image
                src="/images/footer-logo.svg"
                alt="Footer Logo"
                width={111}
                height={40}
                // Static SVG: nothing to resize, keep it off /_next/image.
                unoptimized
                className="footer-logo object-contain"
              />
            </div>
            <div className="order-1 flex flex-col md:flex-row items-center justify-center gap-3 md:order-2 md:justify-end md:gap-6">
              {shouldShowLanguagePicker && <LanguagePicker />}
              <div className="footer-links flex flex-wrap items-center justify-center gap-3 md:gap-6">
                {renderFooterLinks()}
              </div>
            </div>
          </div>
        </CardContent>
      </StyledFooter>
      <FooterLinkModal />
    </>
  );
};

export default Footer;
